import { useState } from 'react'
import { AlertTriangle, CheckCircle2, MapPin } from 'lucide-react'
import { plainRefusal } from '@/utils/plainRefusal'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Pie,
  PieChart,
  Radar,
  RadarChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  CHART_AXIS_TICK,
  CHART_GRID_STROKE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '@/config/chartTheme'
import {
  NEARBY_CATEGORY_LABELS,
  type PropertySiteIntel,
  type DemographicRing,
} from '@/services/dossierContext'

/** How each ring metric is drawn.
 *
 *  Seven metrics drawn seven identical ways is seven charts a reader has to
 *  read the heading of to tell apart — the shape carries no information, so
 *  the eye has nothing to navigate by. Each metric now has the form its own
 *  quantity actually wants (a level over distance is an area, a share is a
 *  ring, a signed change is a diverging bar) and its own hue, so the card is
 *  identifiable before the label is read. The hue is decoration; the shape
 *  is not.
 */
type ChartKind = 'area' | 'column' | 'radial' | 'hbar' | 'diverging' | 'line' | 'donut'

type Metric = {
  key: keyof DemographicRing
  label: string
  unit?: string
  chart: ChartKind
  color: string
  /** Second stop for the fills that carry a gradient. */
  color2: string
}

const metrics: Metric[] = [
  { key: 'population', label: 'Population', chart: 'area', color: '#22d3ee', color2: '#0e7490' },
  { key: 'households', label: 'Households', chart: 'column', color: '#a78bfa', color2: '#6d28d9' },
  { key: 'medianAge', label: 'Median age', unit: 'years', chart: 'radial', color: '#fbbf24', color2: '#b45309' },
  {
    key: 'medianHouseholdIncome',
    label: 'Median household income',
    unit: '$',
    chart: 'hbar',
    color: '#34d399',
    color2: '#047857',
  },
  { key: 'populationGrowthPct', label: 'Population growth', unit: '%', chart: 'diverging', color: '#fb7185', color2: '#4ade80' },
  { key: 'householdGrowthPct', label: 'Household growth', unit: '%', chart: 'line', color: '#e879f9', color2: '#a21caf' },
  { key: 'daytimeJobs', label: 'Daytime jobs', chart: 'donut', color: '#fb923c', color2: '#c2410c' },
]

/** Nine categories, nine hues — the amenity radar reads as a shape per market
 *  rather than nine bars of the same gold. */
const NEARBY_COLORS = [
  '#f5c451', // grocery — gold
  '#fb7185', // dining
  '#38bdf8', // retail
  '#34d399', // pharmacy
  '#a78bfa', // transit
  '#fbbf24', // schools
  '#f472b6', // universities
  '#2dd4bf', // healthcare
  '#84cc16', // parks
]

/** The colour of ring `index` in the two charts that give each ring its own
 *  wedge or arc. The readout below the chart repeats it, because a donut with
 *  no key is three colours and no way to tell which ring is which. */
const ringColor = (metric: Metric, index: number) =>
  [metric.color, '#f59e0b', metric.color2][index % 3]

const format = (value: unknown, unit?: string) =>
  typeof value !== 'number'
    ? 'Not recorded'
    : `${unit === '$' ? '$' : ''}${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit === '%' ? '%' : unit === 'years' ? ' years' : ''}`

const compact = (value: number) =>
  Math.abs(value) >= 1000
    ? `${(value / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`
    : value.toLocaleString(undefined, { maximumFractionDigits: 1 })

const PANEL = 'rounded-lg border border-amber-300/15 bg-black/30'
const HEADING = 'font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-300/90'

const tooltipProps = {
  contentStyle: CHART_TOOLTIP_STYLE,
  labelStyle: CHART_TOOLTIP_LABEL_STYLE,
  itemStyle: CHART_TOOLTIP_ITEM_STYLE,
  cursor: { fill: 'rgba(255,255,255,0.04)' },
} as const

type Point = { miles: number; ring: string; value: number | null }

/** The chart body for one metric. Null rings are dropped rather than plotted
 *  as zero — a ring with no ACS answer is not a ring with nobody in it. */
function MetricChart({ metric, points }: { metric: Metric; points: Point[] }) {
  const drawable = points.filter((p): p is Point & { value: number } => p.value !== null)
  const gradientId = `loc-grad-${String(metric.key)}`
  const fmt = (value: unknown) => format(value, metric.unit)

  if (drawable.length === 0) {
    return (
      <p className="flex h-full items-center text-[10px] text-stone-600">
        No ring recorded a value for this metric.
      </p>
    )
  }

  switch (metric.chart) {
    case 'area':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={drawable} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={metric.color} stopOpacity={0.75} />
                <stop offset="100%" stopColor={metric.color2} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis dataKey="ring" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} width={38} tickFormatter={compact} />
            <Tooltip {...tooltipProps} formatter={(v) => [fmt(v), metric.label]} />
            <Area
              isAnimationActive={false}
              type="monotone"
              dataKey="value"
              stroke={metric.color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={{ r: 3, fill: metric.color, stroke: '#000' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )
    case 'column':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={drawable} margin={{ top: 14, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={metric.color} stopOpacity={1} />
                <stop offset="100%" stopColor={metric.color2} stopOpacity={0.85} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis dataKey="ring" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Tooltip {...tooltipProps} formatter={(v) => [fmt(v), metric.label]} />
            <Bar isAnimationActive={false} dataKey="value" fill={`url(#${gradientId})`} radius={[4, 4, 0, 0]} maxBarSize={38}>
              <LabelList dataKey="value" position="top" fill="#d6d3d1" fontSize={9} formatter={(v: unknown) => compact(Number(v))} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )
    case 'radial': {
      // Concentric arcs for a metric read at concentric distances — the one
      // shape on the page that is literally the geometry it describes.
      const max = Math.max(...drawable.map((p) => p.value)) * 1.15
      // Reversed so the widest ring is the outer arc, and coloured from the
      // un-reversed order so the key below still reads 1 / 2 / 3.
      const data = [...drawable].reverse().map((p) => ({
        ...p,
        fill: ringColor(metric, drawable.indexOf(p)),
      }))
      return (
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart data={data} innerRadius="35%" outerRadius="100%" startAngle={90} endAngle={-270}>
            <PolarAngleAxis type="number" domain={[0, max]} tick={false} />
            <RadialBar isAnimationActive={false} dataKey="value" background={{ fill: 'rgba(255,255,255,0.05)' }} cornerRadius={6} />
            <Tooltip {...tooltipProps} cursor={false} formatter={(v, _n, item) => [fmt(v), `${(item?.payload as Point)?.ring ?? ''}`]} />
          </RadialBarChart>
        </ResponsiveContainer>
      )
    }
    case 'hbar':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={drawable} layout="vertical" margin={{ top: 2, right: 46, bottom: 2, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={metric.color2} stopOpacity={0.9} />
                <stop offset="100%" stopColor={metric.color} stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART_GRID_STROKE} horizontal={false} />
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="ring"
              width={40}
              tick={CHART_AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip {...tooltipProps} formatter={(v) => [fmt(v), metric.label]} />
            <Bar isAnimationActive={false} dataKey="value" fill={`url(#${gradientId})`} radius={[0, 4, 4, 0]} maxBarSize={18}>
              <LabelList
                dataKey="value"
                position="right"
                fill="#d6d3d1"
                fontSize={9}
                formatter={(v: unknown) => `$${compact(Number(v))}`}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )
    case 'diverging':
      // Growth is signed. One fill for both directions hides the only thing
      // the reader is looking for, so the sign picks the colour.
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={drawable} layout="vertical" margin={{ top: 2, right: 40, bottom: 2, left: 0 }}>
            <CartesianGrid stroke={CHART_GRID_STROKE} horizontal={false} />
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="ring" width={40} tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
            <Tooltip {...tooltipProps} formatter={(v) => [fmt(v), metric.label]} />
            <Bar isAnimationActive={false} dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={18}>
              {drawable.map((p) => (
                <Cell key={p.miles} fill={p.value < 0 ? metric.color : metric.color2} />
              ))}
              <LabelList
                dataKey="value"
                position="right"
                fill="#d6d3d1"
                fontSize={9}
                formatter={(v: unknown) => `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(1)}%`}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )
    case 'line':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={drawable} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis dataKey="ring" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} width={38} tickFormatter={(v) => `${Number(v).toFixed(0)}%`} />
            <Tooltip {...tooltipProps} cursor={{ stroke: metric.color, strokeOpacity: 0.3 }} formatter={(v) => [fmt(v), metric.label]} />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="value"
              stroke={metric.color}
              strokeWidth={2.5}
              dot={{ r: 4, fill: metric.color2, stroke: metric.color, strokeWidth: 2 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )
    case 'donut':
    default:
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip {...tooltipProps} cursor={false} formatter={(v, _n, item) => [fmt(v), `${(item?.payload as Point)?.ring ?? ''}`]} />
            <Pie
              isAnimationActive={false}
              data={drawable}
              dataKey="value"
              nameKey="ring"
              innerRadius="48%"
              outerRadius="82%"
              paddingAngle={3}
              stroke="#000"
              strokeWidth={1}
            >
              {drawable.map((p, i) => (
                <Cell key={p.miles} fill={ringColor(metric, i)} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      )
  }
}

/** One metric across the 1 / 2 / 3-mile rings, drawn in the form that metric
 *  wants, with the public-record (county / place) figure above for context and
 *  the ring figures spelled out below it — the chart is the shape, the row is
 *  the number, and neither stands in for the other. */
function RingMetricCard({
  metric,
  rings,
  countyValue,
}: {
  metric: Metric
  rings: DemographicRing[]
  countyValue: unknown
}) {
  const points: Point[] = rings.map((ring) => ({
    miles: ring.miles,
    ring: `${ring.miles} mi`,
    value: typeof ring[metric.key] === 'number' ? (ring[metric.key] as number) : null,
  }))
  // The arc and wedge charts colour by ring rather than by metric, so the
  // readout doubles as their key. The rest label their own axis already.
  const keyed = metric.chart === 'radial' || metric.chart === 'donut'
  return (
    <section className={`${PANEL} flex min-w-0 flex-col p-3`}>
      <h3 className={HEADING} style={{ color: metric.color }}>
        {metric.label}
      </h3>
      <p className="mt-1 text-[10px] text-stone-500">
        Public-record geography:{' '}
        <span className="font-mono text-stone-300">{format(countyValue, metric.unit)}</span>
      </p>
      {points.length === 0 ? (
        <p className="mt-3 text-[10px] text-stone-600">No radius series recorded for this metric.</p>
      ) : (
        <>
          <div className="mt-2 h-28 min-w-0">
            <MetricChart metric={metric} points={points} />
          </div>
          <dl className="mt-2 space-y-0.5 border-t border-white/[0.06] pt-1.5">
            {points.map((point, index) => (
              <div key={point.miles} className="flex items-baseline justify-between gap-2">
                <dt className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-stone-500">
                  {keyed ? (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ background: ringColor(metric, index) }}
                    />
                  ) : null}
                  {point.ring}
                </dt>
                <dd
                  className={`font-mono text-[11px] ${point.value === null ? 'text-stone-600' : 'text-stone-100'}`}
                >
                  {format(point.value, metric.unit)}
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </section>
  )
}

export function LocationIntelligenceDetail({
  intel,
}: {
  intel: PropertySiteIntel | null | undefined
}) {
  const [category, setCategory] = useState('grocery')
  const groups = NEARBY_CATEGORY_LABELS.map(({ kind, label }, index) => ({
    kind,
    label,
    color: NEARBY_COLORS[index % NEARBY_COLORS.length],
    places:
      intel?.nearby?.[kind] ??
      (kind === 'transit'
        ? intel?.transit
        : kind === 'school'
          ? intel?.schools
          : []) ??
      [],
    count: intel?.nearbyCounts?.[kind] ?? intel?.nearby?.[kind]?.length ?? null,
    capped: intel?.nearbyCapped?.[kind] ?? false,
  }))
  const selected = groups.find((g) => g.kind === category) ?? groups[0]
  const rings = intel?.rings ?? []
  const sources = intel?.sources ?? []
  const address = intel?.formattedAddress ?? intel?.addressUsed ?? 'Location not recorded'
  const hasRingValues = rings.some((ring) =>
    metrics.some((metric) => typeof ring[metric.key] === 'number'),
  )
  const noCoordinate = intel?.lat == null || intel?.lng == null
  const blockers = sources.filter((source) => !source.ok)
  const records: [string, unknown][] = [
    ['County', intel?.county],
    ['State', intel?.stateCode],
    ['County FIPS', intel?.countyFips],
    ['Neighborhood', intel?.neighborhood],
    ['Parcel number', intel?.parcelNumber],
    ['Parcel owner', intel?.parcelOwner],
    ['Parcel acres', intel?.parcelAcres],
    ['Zoning', intel?.parcelZoning],
    ['Land use', intel?.parcelLandUse],
    ['Flood zone', intel?.floodZone],
    ['Flood zone detail', plainRefusal(intel?.floodZoneDetail)],
  ]
  return (
    <div
      className="space-y-4 text-xs text-stone-200"
      data-testid="location-intelligence-detail"
    >
      {/* Where, at what geography, and which sources answered. */}
      <div className={`${PANEL} flex flex-wrap items-start justify-between gap-3 px-4 py-3`}>
        <div className="flex min-w-0 items-start gap-2.5">
          <MapPin size={16} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-stone-100">{address}</div>
            <p className="mt-0.5 text-stone-400">
              {intel?.populationGeography ?? 'Geography not resolved'}. Counts and
              estimates retain their source geography.
            </p>
          </div>
        </div>
        {sources.length ? (
          <div className="flex max-w-full flex-wrap justify-end gap-1.5">
            {sources.map((source) => (
              <span
                key={source.source}
                title={plainRefusal(source.detail) ?? undefined}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                  source.ok
                    ? 'border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300'
                    : 'border-amber-500/30 bg-amber-500/[0.06] text-amber-300'
                }`}
              >
                {source.ok ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
                {source.source}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {/* Why the rings are empty, in the sources' own words. Nothing is
          estimated in their place. */}
      {!hasRingValues ? (
        <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.04] px-4 py-3">
          <div className="flex items-center gap-2 text-[12px] font-semibold text-amber-200">
            <AlertTriangle size={14} /> Radius demographics are not available for this deal yet
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-stone-300">
            {noCoordinate ? (
              <li>
                No coordinate on record: {address} does not resolve to a site. A
                street address on the deal is needed to draw the rings.
              </li>
            ) : null}
            {blockers.map((source) => (
              <li key={source.source}>
                <span className="text-stone-100">{source.source}:</span>{' '}
                {plainRefusal(source.detail) ?? 'Unavailable'}
              </li>
            ))}
            {!noCoordinate && blockers.length === 0 ? (
              <li>The sources answered, but no block group fell inside the rings.</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <RingMetricCard
            key={metric.key}
            metric={metric}
            rings={rings}
            countyValue={intel?.[metric.key as keyof PropertySiteIntel]}
          />
        ))}
        <section className={`${PANEL} p-3`}>
          <h3 className={HEADING}>How the rings are built</h3>
          <p className="mt-2 leading-relaxed text-stone-400">
            Radius figures are cumulative ACS block-group estimates, not separate
            annuli: every block group whose centroid lies inside the ring is counted
            whole, not apportioned by area; medians are land-area-weighted averages.
            Growth measures use the recorded ACS comparison period. Missing values
            are not zero.
          </p>
          {rings.map((ring) => (
            <p key={ring.miles} className="mt-1 text-[10px] text-stone-500">
              {ring.miles} mi · {ring.blockGroupCount} block groups · {ring.note}
            </p>
          ))}
        </section>
      </div>

      {/* Every ring figure on one row each — the cross-metric read the cards
          cannot give, and the table the Property dashboard used to carry
          before the tiles there had to give up their height for it. */}
      {rings.length ? (
        <section className={`${PANEL} p-3`} data-testid="demographic-rings">
          <h3 className={HEADING}>1 / 2 / 3-mile rings · ACS block-group estimate</h3>
          <table className="mt-2 w-full text-left text-[11px]">
            <thead className="font-mono text-[9px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="pb-1 font-normal">Ring</th>
                {metrics.map((metric) => (
                  <th key={metric.key} className="pb-1 text-right font-normal" style={{ color: metric.color }}>
                    {metric.label}
                  </th>
                ))}
                <th className="pb-1 text-right font-normal">Block groups</th>
              </tr>
            </thead>
            <tbody>
              {rings.map((ring) => (
                <tr key={ring.miles} className="border-t border-white/[0.06] text-stone-300" title={ring.note}>
                  <td className="py-1">{ring.miles} mi</td>
                  {metrics.map((metric) => (
                    <td
                      key={metric.key}
                      className={`py-1 text-right font-mono ${ring[metric.key] == null ? 'text-stone-600' : ''}`}
                    >
                      {ring[metric.key] == null ? '—' : format(ring[metric.key], metric.unit)}
                    </td>
                  ))}
                  <td className="py-1 text-right font-mono">{ring.blockGroupCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-[9px] text-stone-600">
            Whole ACS block groups by centroid, summed. Not a CoStar radius. Daytime jobs need a LEHD LODES extract.
          </p>
        </section>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <section className={`${PANEL} p-3`}>
          <h3 className={HEADING}>Nearby amenities</h3>
          {/* A radar, not a ninth bar chart: the amenity mix is a profile, and
              a profile is a shape you compare between submarkets at a glance.
              The per-category dots carry the category's own colour so the
              table beside it and the tiles on the dashboard agree. */}
          <div className="mt-2 h-60">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={groups} outerRadius="72%">
                <defs>
                  <radialGradient id="loc-amenity-fill">
                    <stop offset="0%" stopColor="#f5c451" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.18} />
                  </radialGradient>
                </defs>
                <PolarGrid stroke="rgba(255,255,255,0.10)" />
                <PolarAngleAxis dataKey="label" tick={{ fill: '#a8a29e', fontSize: 9 }} />
                <PolarRadiusAxis
                  angle={90}
                  tick={{ fill: '#57534e', fontSize: 8 }}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip {...tooltipProps} cursor={false} formatter={(v) => [v ?? 'Not recorded', 'Places']} />
                <Radar
                  isAnimationActive={false}
                  name="Places"
                  dataKey="count"
                  stroke="#f5c451"
                  strokeWidth={2}
                  fill="url(#loc-amenity-fill)"
                  dot={(props: { cx?: number; cy?: number; index?: number; key?: string }) => (
                    <circle
                      key={props.key ?? props.index}
                      cx={props.cx}
                      cy={props.cy}
                      r={3.5}
                      fill={NEARBY_COLORS[(props.index ?? 0) % NEARBY_COLORS.length]}
                      stroke="#000"
                      strokeWidth={1}
                    />
                  )}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {groups.map((group) => (
              <li key={group.kind} className="flex items-center gap-1 text-[9.5px] text-stone-400">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: group.color }} />
                {group.label}
                <span className="font-mono text-stone-200">
                  {group.count ?? '—'}
                  {group.capped ? '+' : ''}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-[10px] text-stone-500">
            Search radius:{' '}
            {intel?.nearbyRadiusM
              ? `${(intel.nearbyRadiusM / 1609.344).toFixed(1)} miles`
              : 'not recorded'}
            . Capped categories show a minimum count.
          </p>
        </section>
        <section className={`${PANEL} flex min-h-0 flex-col p-3`}>
          <label className={`flex items-center justify-between gap-2 ${HEADING}`}>
            Places by category
            <select
              className="rounded border border-stone-700 bg-[#111] px-2 py-1 font-sans text-[11px] normal-case tracking-normal text-stone-200"
              value={selected?.kind}
              onChange={(e) => setCategory(e.target.value)}
            >
              {groups.map((g) => (
                <option key={g.kind} value={g.kind}>
                  {g.label} ({g.count ?? '—'}
                  {g.capped ? '+' : ''})
                </option>
              ))}
            </select>
          </label>
          <div className="mt-2 max-h-64 overflow-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="font-mono text-[9px] uppercase tracking-wider text-stone-500">
                  <th className="py-1.5 font-normal">Place</th>
                  <th className="py-1.5 font-normal">Address / vicinity</th>
                </tr>
              </thead>
              <tbody>
                {selected?.places.map((place, i) => (
                  <tr key={`${place.name}-${i}`} className="border-t border-white/[0.06]">
                    <td className="py-1.5 pr-3 text-stone-200">{place.name}</td>
                    <td className="text-stone-400">{place.vicinity ?? 'Not recorded'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!selected?.places.length && (
              <p className="py-6 text-center text-stone-500">
                No individual places recorded for this category.
              </p>
            )}
          </div>
        </section>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <section className={`${PANEL} p-3`}>
          <h3 className={HEADING}>Public records</h3>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            {records.map(([label, value]) => (
              <div key={label} className="min-w-0 border-b border-white/[0.05] pb-1.5">
                <dt className="font-mono text-[9px] uppercase tracking-wide text-stone-500">{label}</dt>
                <dd
                  className={`mt-0.5 truncate ${value == null ? 'text-stone-600' : 'text-stone-200'}`}
                  title={value == null ? undefined : String(value)}
                >
                  {value == null ? 'Not recorded' : String(value)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
        <section className={`${PANEL} p-3`}>
          <h3 className={HEADING}>Sources and coverage</h3>
          {sources.length ? (
            <table className="mt-2 w-full text-left">
              <tbody>
                {sources.map((s) => (
                  <tr key={s.source} className="border-t border-white/[0.06] align-top">
                    <th className="py-1.5 pr-3 font-normal text-stone-200">{s.source}</th>
                    <td className="py-1.5 pr-3">
                      <span className={`rounded border px-1.5 py-0.5 text-[9px] ${s.ok ? 'border-emerald-500/30 text-emerald-300' : 'border-amber-500/30 text-amber-300'}`}>
                        {s.ok ? 'Available' : 'Unavailable'}
                      </span>
                    </td>
                    <td className="py-1.5 text-stone-400">{plainRefusal(s.detail) ?? 'No additional coverage note'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="mt-2 text-stone-500">No source reported on this lookup.</p>
          )}
        </section>
      </div>
    </div>
  )
}
